import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import toast from '../../design-system/toast';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { useOrganization } from '../../contexts/OrganizationContext';

const SCAN_TYPES = [
  { value: 'semgrep_scan', label: 'Semgrep' },
  { value: 'secrets_scan', label: 'Secrets' },
  { value: 'deep_ai_scan', label: 'Deep AI Scan' },
];

const SEVERITIES = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

type SlaRule = {
  scan_type: string;
  severity: string;
  target_business_days: number;
};

type SlaConfig = {
  id: string;
  version: number;
  effective_at: string;
  created_at: string;
  created_by: string;
  notes?: string | null;
  rules: SlaRule[];
};

type SlaConfigHistoryResponse = {
  current?: SlaConfig | null;
  history?: SlaConfig[] | null;
};

type BusinessCalendar = {
  timezone: string;
  working_days: number[];
  calendar_type: 'manual' | 'regional';
  region_code?: string | null;
  added_holidays: string[];
  removed_holidays: string[];
};

const WORKING_DAYS = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

interface OrgSlaSectionProps {
  organizationId: string;
}

const parseHolidayInput = (value: string): string[] => {
  return value
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const serializeHolidayInput = (value: string[] = []) => value.join('\n');

const OrgSlaSection: React.FC<OrgSlaSectionProps> = ({ organizationId }) => {
  const api = useAuthenticatedApi();
  const { isAdmin } = useOrganization();

  const [slaHistory, setSlaHistory] = useState<SlaConfig[]>([]);
  const [rules, setRules] = useState<SlaRule[]>([]);
  const [notes, setNotes] = useState('');
  const [isSavingRules, setIsSavingRules] = useState(false);

  const [calendar, setCalendar] = useState<BusinessCalendar>({
    timezone: 'UTC',
    working_days: [0, 1, 2, 3, 4],
    calendar_type: 'manual',
    region_code: '',
    added_holidays: [],
    removed_holidays: [],
  });
  const [calendarAddedText, setCalendarAddedText] = useState('');
  const [calendarRemovedText, setCalendarRemovedText] = useState('');
  const [isSavingCalendar, setIsSavingCalendar] = useState(false);

  const loadData = async () => {
    try {
      const slaResponse = await api.get<SlaConfigHistoryResponse>(
        `${apiEndpoints.sla.config(organizationId)}?include_history=true`
      );
      setSlaHistory(slaResponse.history ?? []);
      setRules(slaResponse.current?.rules ?? []);
      setNotes(slaResponse.current?.notes ?? '');

      const calendarResponse = await api.get<BusinessCalendar>(
        apiEndpoints.sla.calendar(organizationId)
      );
      setCalendar(calendarResponse);
      setCalendarAddedText(serializeHolidayInput(calendarResponse.added_holidays));
      setCalendarRemovedText(serializeHolidayInput(calendarResponse.removed_holidays));
    } catch (err: any) {
      console.error('Failed to load SLA settings:', err);
      toast.error(err.data?.detail || err.message || 'Failed to load SLA settings');
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const addRule = () => {
    setRules((prev) => [
      ...prev,
      { scan_type: 'semgrep_scan', severity: 'high', target_business_days: 5 },
    ]);
  };

  const updateRule = (index: number, updates: Partial<SlaRule>) => {
    setRules((prev) =>
      prev.map((rule, idx) => (idx === index ? { ...rule, ...updates } : rule))
    );
  };

  const removeRule = (index: number) => {
    setRules((prev) => prev.filter((_, idx) => idx !== index));
  };

  const saveRules = async () => {
    if (!isAdmin) return;
    try {
      setIsSavingRules(true);
      const response = await api.put<SlaConfig>(
        apiEndpoints.sla.config(organizationId),
        { rules, notes: notes?.trim() || null }
      );
      await loadData();
      toast.success('SLA configuration updated');
    } catch (err: any) {
      console.error('Failed to update SLA config:', err);
      toast.error(err.data?.detail || err.message || 'Failed to update SLA config');
    } finally {
      setIsSavingRules(false);
    }
  };

  const toggleWorkingDay = (day: number) => {
    setCalendar((prev) => {
      const exists = prev.working_days.includes(day);
      const nextDays = exists
        ? prev.working_days.filter((value) => value !== day)
        : [...prev.working_days, day];
      return { ...prev, working_days: nextDays };
    });
  };

  const saveCalendar = async () => {
    if (!isAdmin) return;
    try {
      setIsSavingCalendar(true);
      const payload = {
        ...calendar,
        added_holidays: parseHolidayInput(calendarAddedText),
        removed_holidays: parseHolidayInput(calendarRemovedText),
        region_code: calendar.calendar_type === 'regional' ? calendar.region_code || null : null,
      };
      const response = await api.put<BusinessCalendar>(
        apiEndpoints.sla.calendar(organizationId),
        payload
      );
      setCalendar(response);
      setCalendarAddedText(serializeHolidayInput(response.added_holidays));
      setCalendarRemovedText(serializeHolidayInput(response.removed_holidays));
      toast.success('Business calendar updated');
    } catch (err: any) {
      console.error('Failed to update business calendar:', err);
      toast.error(err.data?.detail || err.message || 'Failed to update business calendar');
    } finally {
      setIsSavingCalendar(false);
    }
  };

  const historyRows = useMemo(() => {
    return slaHistory.map((config) => (
      <div
        key={config.id}
        className="flex items-center justify-between border border-ui-border-primary rounded-lg p-3"
      >
        <div>
          <p className="text-sm font-medium text-ui-text-primary">
            Version {config.version}
          </p>
          <p className="text-xs text-ui-text-tertiary">
            Effective {new Date(config.effective_at).toLocaleString()}
          </p>
        </div>
        <div className="text-xs text-ui-text-tertiary">
          {config.rules.length} rules
        </div>
      </div>
    ));
  }, [slaHistory]);

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-ui-text-primary">SLA Settings</h3>
        <p className="text-sm text-ui-text-secondary mt-1">
          Define remediation timelines by scan type and severity, plus the business calendar used to measure SLA time.
        </p>
      </div>

      <div className="bg-ui-background-tertiary rounded-lg border border-ui-border-primary p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-ui-text-primary">SLA Targets</h4>
            <p className="text-xs text-ui-text-tertiary">
              Use business days. Each update creates a new version.
            </p>
          </div>
          <button
            onClick={addRule}
            disabled={!isAdmin}
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg bg-ui-background-tertiary text-ui-text-primary hover:bg-ui-background-tertiary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-ui-background-tertiary dark:disabled:hover:bg-ui-background-tertiary"
          >
            <Plus className="w-4 h-4" />
            Add Rule
          </button>
        </div>

        <div className="space-y-3">
          {rules.length === 0 && (
            <p className="text-sm text-ui-text-tertiary">No rules yet.</p>
          )}
          {rules.map((rule, index) => (
            <div
              key={`${rule.scan_type}-${rule.severity}-${index}`}
              className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr_1fr_auto] gap-3 items-center"
            >
              <select
                value={rule.scan_type}
                onChange={(e) => updateRule(index, { scan_type: e.target.value })}
                disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-sm"
              >
                {SCAN_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <select
                value={rule.severity}
                onChange={(e) => updateRule(index, { severity: e.target.value })}
                disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-sm"
              >
                {SEVERITIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={0}
                value={rule.target_business_days}
                onChange={(e) => updateRule(index, { target_business_days: Number(e.target.value) })}
                disabled={!isAdmin}
                className="w-full px-3 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-sm"
              />
              <button
                onClick={() => removeRule(index)}
                disabled={!isAdmin}
                className="p-2 rounded-lg hover:bg-ui-background-tertiary text-ui-text-secondary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div>
          <label className="block text-xs font-medium text-ui-text-secondary mb-2">
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!isAdmin}
            rows={2}
            className="w-full px-3 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-sm"
          />
        </div>

        <button
          onClick={saveRules}
          disabled={isSavingRules || !isAdmin}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-primary text-black text-sm font-medium hover:bg-brand-primaryDark disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          {isSavingRules ? 'Saving...' : 'Save SLA Config'}
        </button>
      </div>

      <div className="bg-ui-background-tertiary rounded-lg border border-ui-border-primary p-6 space-y-4">
        <div>
          <h4 className="text-sm font-semibold text-ui-text-primary">Business Calendar</h4>
          <p className="text-xs text-ui-text-tertiary">
            Regional calendars use holiday sets from the holidays library with optional overrides.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-ui-text-secondary mb-2">
              Timezone
            </label>
            <input
              type="text"
              value={calendar.timezone}
              onChange={(e) => setCalendar((prev) => ({ ...prev, timezone: e.target.value }))}
              disabled={!isAdmin}
              className="w-full px-3 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ui-text-secondary mb-2">
              Calendar Type
            </label>
            <select
              value={calendar.calendar_type}
              onChange={(e) =>
                setCalendar((prev) => ({
                  ...prev,
                  calendar_type: e.target.value as BusinessCalendar['calendar_type'],
                }))
              }
              disabled={!isAdmin}
              className="w-full px-3 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-sm"
            >
              <option value="manual">Manual</option>
              <option value="regional">Regional</option>
            </select>
          </div>
        </div>

        {calendar.calendar_type === 'regional' && (
          <div>
            <label className="block text-xs font-medium text-ui-text-secondary mb-2">
              Region Code (e.g., US, US-CA)
            </label>
            <input
              type="text"
              value={calendar.region_code ?? ''}
              onChange={(e) => setCalendar((prev) => ({ ...prev, region_code: e.target.value }))}
              disabled={!isAdmin}
              className="w-full px-3 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-sm"
            />
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-ui-text-secondary mb-2">
            Working Days
          </label>
          <div className="flex flex-wrap gap-2">
            {WORKING_DAYS.map((day) => (
              <button
                key={day.value}
                onClick={() => toggleWorkingDay(day.value)}
                disabled={!isAdmin}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                  calendar.working_days.includes(day.value)
                    ? 'bg-brand-primary text-black border-brand-primary'
                    : 'bg-ui-background-secondary text-ui-text-secondary border-ui-border-primary'
                }`}
              >
                {day.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-ui-text-secondary mb-2">
              Added Holidays (YYYY-MM-DD)
            </label>
            <textarea
              value={calendarAddedText}
              onChange={(e) => setCalendarAddedText(e.target.value)}
              disabled={!isAdmin}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ui-text-secondary mb-2">
              Removed Holidays (YYYY-MM-DD)
            </label>
            <textarea
              value={calendarRemovedText}
              onChange={(e) => setCalendarRemovedText(e.target.value)}
              disabled={!isAdmin}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-ui-border-primary bg-ui-background-secondary text-sm"
            />
          </div>
        </div>

        <button
          onClick={saveCalendar}
          disabled={isSavingCalendar || !isAdmin}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-primary text-black text-sm font-medium hover:bg-brand-primaryDark disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          {isSavingCalendar ? 'Saving...' : 'Save Calendar'}
        </button>
      </div>

      <div className="bg-ui-background-tertiary rounded-lg border border-ui-border-primary p-6 space-y-3">
        <h4 className="text-sm font-semibold text-ui-text-primary">SLA Config History</h4>
        {historyRows.length > 0 ? historyRows : (
          <p className="text-sm text-ui-text-tertiary">No previous versions yet.</p>
        )}
      </div>
    </div>
  );
};

export default OrgSlaSection;
